import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';

export interface DropdownOption<T extends string | number = string> {
  value: T;
  label: string;
  /** Secondary line under the label. */
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface DropdownProps<T extends string | number = string> {
  value: T;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  isDark: boolean;
  /** Visible label rendered before the trigger. */
  label?: string;
  /** Announced to screen readers when there is no visible label. */
  ariaLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Let the trigger fill its container instead of hugging its content. */
  block?: boolean;
  /** Minimum menu width in px. Defaults to the trigger width. */
  menuMinWidth?: number;
  align?: 'left' | 'right';
  className?: string;
}

const TRIGGER_SIZE = {
  sm: 'h-7 pl-2.5 pr-1.5 text-[12px] gap-1 rounded-lg',
  md: 'h-9 pl-3 pr-2 text-[13px] gap-1.5 rounded-[10px]',
};

export function Dropdown<T extends string | number = string>({
  value, options, onChange, isDark,
  label, ariaLabel, placeholder = 'Select…', disabled = false,
  size = 'md', block = false, menuMinWidth, align = 'left', className = '',
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const { triggerRef, position } = useDropdownPosition(open);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typeAhead = useRef({ query: '', at: 0 });
  const listboxId = useRef(`dd-${Math.random().toString(36).slice(2, 9)}`).current;

  const selectedIndex = useMemo(
    () => options.findIndex((o) => o.value === value),
    [options, value],
  );
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  /* Opening lands the highlight on the current value, as a native select does. */
  const openMenu = useCallback(() => {
    if (disabled) return;
    setHighlight(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }, [disabled, selectedIndex]);

  const closeMenu = useCallback((refocus = true) => {
    setOpen(false);
    setHighlight(-1);
    if (refocus) buttonRef.current?.focus();
  }, []);

  const commit = useCallback((idx: number) => {
    const opt = options[idx];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    closeMenu();
  }, [options, onChange, closeMenu]);

  /* Step over disabled options rather than landing on them. */
  const step = useCallback((from: number, dir: 1 | -1) => {
    const n = options.length;
    for (let i = 1; i <= n; i++) {
      const next = (from + dir * i + n * n) % n;
      if (!options[next]?.disabled) return next;
    }
    return from;
  }, [options]);

  useEffect(() => {
    if (!open || highlight < 0 || !listRef.current) return;
    listRef.current
      .querySelectorAll('[data-opt]')[highlight]
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  /* Close on outside click, scroll of an ancestor, or window resize. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || listRef.current?.contains(t)) return;
      closeMenu(false);
    };
    const onLeave = () => closeMenu(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onLeave);
    window.addEventListener('scroll', onLeave, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onLeave);
      window.removeEventListener('scroll', onLeave, true);
    };
  }, [open, closeMenu, triggerRef]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }

    switch (e.key) {
      case 'Escape': e.preventDefault(); closeMenu(); return;
      case 'Tab': closeMenu(false); return;
      case 'ArrowDown': e.preventDefault(); setHighlight((h) => step(h < 0 ? -1 : h, 1)); return;
      case 'ArrowUp': e.preventDefault(); setHighlight((h) => step(h < 0 ? 0 : h, -1)); return;
      case 'Home': e.preventDefault(); setHighlight(step(-1, 1)); return;
      case 'End': e.preventDefault(); setHighlight(step(0, -1)); return;
      case 'Enter':
      case ' ': e.preventDefault(); commit(highlight); return;
      default: break;
    }

    if (e.key.length === 1 && /\S/.test(e.key)) {
      const now = Date.now();
      const q = now - typeAhead.current.at > 700 ? e.key : typeAhead.current.query + e.key;
      typeAhead.current = { query: q, at: now };
      const hit = options.findIndex(
        (o) => !o.disabled && o.label.toLowerCase().startsWith(q.toLowerCase()),
      );
      if (hit >= 0) setHighlight(hit);
    }
  };

  const menuWidth = Math.max(menuMinWidth ?? 0, position.width || 0);

  return (
    <div className={['inline-flex items-center gap-2', block ? 'w-full' : '', className].join(' ')}>
      {label && (
        <span className={['flex-shrink-0 text-[11.5px] font-medium', isDark ? 'text-slate-400' : 'text-slate-500'].join(' ')}>
          {label}
        </span>
      )}

      <div ref={triggerRef} className={block ? 'relative w-full' : 'relative'}>
        <button
          ref={buttonRef}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-label={ariaLabel}
          disabled={disabled}
          onClick={() => (open ? closeMenu() : openMenu())}
          onKeyDown={onKeyDown}
          className={[
            'inline-flex items-center justify-between font-semibold',
            'transition-colors duration-150 outline-none',
            'focus-visible:ring-2 focus-visible:ring-brand-500/45',
            'disabled:opacity-45 disabled:pointer-events-none',
            block ? 'w-full' : '',
            TRIGGER_SIZE[size],
            isDark
              ? 'bg-white/[0.05] text-slate-200 ring-1 ring-inset ring-white/[0.10] hover:bg-white/[0.09]'
              : 'bg-white text-slate-700 ring-1 ring-inset ring-slate-300/80 hover:bg-slate-50 hover:ring-slate-400/70',
            open ? (isDark ? 'ring-brand-400/50' : 'ring-brand-500/60') : '',
          ].join(' ')}
        >
          <span className="flex min-w-0 items-center gap-2">
            {selected?.icon}
            <span className={['truncate', !selected ? (isDark ? 'text-slate-500' : 'text-slate-400') : ''].join(' ')}>
              {/* If the current value is not among the options, show it verbatim
                  rather than silently displaying a different option — which is
                  what a native <select> does, and how a value/options mismatch
                  stays invisible. */}
              {selected?.label ?? (value !== undefined && value !== null && value !== '' ? String(value) : placeholder)}
            </span>
          </span>
          <ChevronDown
            size={size === 'sm' ? 12 : 14}
            aria-hidden="true"
            className={[
              'ml-1 flex-shrink-0 transition-transform duration-200',
              open ? 'rotate-180' : '',
              isDark ? 'text-slate-400' : 'text-slate-400',
            ].join(' ')}
          />
        </button>
      </div>

      {open && position.ready && createPortal(
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-activedescendant={highlight >= 0 ? `${listboxId}-${highlight}` : undefined}
          tabIndex={-1}
          style={{
            position: 'fixed',
            zIndex: 10000,
            maxHeight: position.maxHeight,
            minWidth: menuWidth,
            ...(align === 'right'
              ? { right: Math.max(6, window.innerWidth - position.left - position.width) }
              : { left: position.left }),
            ...(position.direction === 'above'
              ? { bottom: position.bottom }
              : { top: position.top }),
          }}
          className={[
            'overflow-y-auto overscroll-contain rounded-xl p-1 animate-fade-in',
            isDark
              ? 'bg-[#111F33] ring-1 ring-inset ring-white/[0.12] shadow-[0_16px_40px_-12px_rgba(0,0,0,.7)]'
              : 'bg-white ring-1 ring-inset ring-slate-200 shadow-[0_16px_40px_-12px_rgba(15,23,42,.22)]',
          ].join(' ')}
        >
          {options.map((o, i) => {
            const isSel = o.value === value;
            const isHi = i === highlight;
            return (
              <div
                key={String(o.value)}
                id={`${listboxId}-${i}`}
                data-opt
                role="option"
                aria-selected={isSel}
                aria-disabled={o.disabled || undefined}
                onMouseEnter={() => !o.disabled && setHighlight(i)}
                onClick={() => commit(i)}
                className={[
                  'flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-[7px]',
                  'text-[12.5px] font-medium transition-colors duration-100',
                  o.disabled ? 'cursor-not-allowed opacity-40' : '',
                  isHi
                    ? isDark ? 'bg-white/[0.09] text-white' : 'bg-slate-100 text-slate-900'
                    : isDark ? 'text-slate-300' : 'text-slate-700',
                ].join(' ')}
              >
                {o.icon && <span className="flex-shrink-0">{o.icon}</span>}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{o.label}</span>
                  {o.description && (
                    <span className={['mt-0.5 block truncate text-[11px]', isDark ? 'text-slate-500' : 'text-slate-400'].join(' ')}>
                      {o.description}
                    </span>
                  )}
                </span>
                <Check
                  size={13}
                  aria-hidden="true"
                  className={[
                    'flex-shrink-0 transition-opacity',
                    isSel ? 'opacity-100' : 'opacity-0',
                    isDark ? 'text-brand-300' : 'text-brand-600',
                  ].join(' ')}
                />
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

export default Dropdown;
